<template>
  <!-- Main Content -->
  <main class="py-4 sm:py-8 sm:p-8">
    <!-- Header -->
    <div class="mb-10 flex items-center justify-between">
      <div>
        <PageTitle>
          Gerencie seus links UTM
        </PageTitle>
        <PageDescription>
          Rastreie suas vendas de forma precisa com links UTM.
        </PageDescription>
      </div>
      <RegisterButton @click="openNewLinkDialog">Cadastrar link UTM</RegisterButton>
    </div>

    <!-- UTM Links Table Card -->
    <TableContainer>
      <!-- Table Header -->
      <div class="mb-6 flex items-center justify-between">
        <h2 class="text-xl font-semibold text-white">Links UTM registrados</h2>
        <div class="flex items-center gap-4"></div>
      </div>

      <!-- Table -->
      <Table class="hidden md:block">
        <TableHeader>
          <TableRow>
            <TableHead
              v-for="header in tableHeaders"
              :key="header.key"
              :class="header.class"
            >
              <div class="flex items-center gap-2">
                {{ header.label }}
                <ArrowsUpDownIcon 
                  v-if="header.sortable"
                  class="h-4 w-4 cursor-pointer text-gray-500 hover:text-white"
                  @click="sortBy(header.key)"
                />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <template v-if="utmLinks">
            <TableFallback v-if="utmLinks.data.length === 0" :colspan="tableHeaders.length" :icon="LinkIcon" title="Nenhum link UTM encontrado" :description="`Clique em 'Cadastrar link UTM' para começar`" />
            <TableRow
              v-for="utmLink in utmLinks.data"
              :key="utmLink.id"
            >
              <TableCell>
                <div class="text-sm font-medium text-white">{{ utmLink.name }}</div>
              </TableCell>
              <TableCell>
                <div class="text-sm font-medium text-white">{{ utmLink.campaignName }}</div>
              </TableCell>
              <TableCell>
                <div class="text-sm text-white">{{ utmLink.source }}</div>
              </TableCell>
              <TableCell>
                <div class="text-sm text-white">{{ utmLink.medium }}</div>
              </TableCell>
              <TableCell>
                <div class="text-sm text-white">{{ utmLink.content }}</div>
              </TableCell>
              <TableCell>
                <div class="text-sm text-white">{{ utmLink.leadStartParameter?.content }}</div>
              </TableCell>
              <TableCell>
                <div class="group flex items-center gap-2">
                  <div class="max-w-md truncate text-sm text-blue-300 hover:text-blue-200 transition-colors">
                    {{botLink}}{{ utmLink.leadStartParameter?.content }}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-7 w-7 group-hover:opacity-100 transition-opacity hover:bg-gray-700"
                    @click="copyLink(`${botLink}${utmLink.leadStartParameter?.content}`)"
                    v-if="copiedLink !== `${botLink}${utmLink.leadStartParameter?.content}`"
                  >
                    <ClipboardIcon class="h-3.5 w-3.5" />
                    <span class="sr-only">Copiar link</span>
                  </Button>
                  <span 
                    v-if="copiedLink === `${botLink}${utmLink.leadStartParameter?.content}`"
                    class="text-xs text-green-400 animate-fade-in-out"
                  >
                    <CheckIcon />
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <div class="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    @click="editUtmLink(utmLink)"
                  >
                    <PencilIcon class="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="hover:text-red-400"
                    @click="deleteLink(utmLink)"
                  >
                    <TrashIcon class="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </template>
          <TableRowSkeleton v-else :columns="tableHeaders.length" />
        </TableBody>
      </Table>

      <!-- Mobile List -->
      <div class="space-y-4 md:hidden text-left" v-if="utmLinks">
        <template v-if="utmLinks.data.length > 0">
          <MobileCard v-for="utmLink in utmLinks.data" :key="utmLink.id">
            <template #header>
               <div class="flex items-center gap-2">
                 <div class="flex flex-col">
                  <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider">Nome</span>
                  <div class="flex items-center gap-2">
                    <span class="text-white font-bold text-lg">{{ utmLink.name }}</span>
                  </div>
                 </div>
               </div>
            </template>
            
            <template #actions>
               <div class="flex flex-col items-end gap-2">
                  <div class="flex items-center gap-1">
                    <button @click="editUtmLink(utmLink)" class="text-zinc-400 hover:text-white p-1">
                      <PencilIcon class="size-5" />
                    </button>
                    <button @click="deleteLink(utmLink)" class="text-zinc-400 hover:text-red-400 p-1">
                      <TrashIcon class="size-5" />
                    </button>
                  </div>
               </div>
            </template>
            
            <template #content>
               <MobileDataLabel label="Campanha">
                   {{ utmLink.campaignName }}
               </MobileDataLabel>
               
               <div class="grid grid-cols-2 gap-4">
                   <MobileDataLabel label="Origem">
                      {{ utmLink.source }}
                   </MobileDataLabel>
                   <MobileDataLabel label="Meio">
                      {{ utmLink.medium }}
                   </MobileDataLabel>
               </div>

               <MobileDataLabel label="Conteúdo">
                   {{ utmLink.content }}
               </MobileDataLabel>

               <MobileDataLabel label="Palavra-Chave">
                   {{ utmLink.leadStartParameter?.content }}
               </MobileDataLabel>

               <MobileDataLabel label="Link">
                   <div class="flex items-center gap-2 bg-white/5 p-2 rounded justify-between">
                      <div class="font-mono text-xs text-blue-300 truncate max-w-[250px]">
                        {{ `${botLink}${utmLink.leadStartParameter?.content}` }}
                      </div>
                      <button
                         @click="copyLink(`${botLink}${utmLink.leadStartParameter?.content}`)"
                         class="rounded-lg p-1.5 text-zinc-400 transition-all hover:bg-white/10 hover:text-white"
                       >
                         <ClipboardIcon v-if="copiedLink !== `${botLink}${utmLink.leadStartParameter?.content}`" class="size-4 text-white" />
                         <CheckIcon v-else class="size-4 text-green-400" />
                       </button>
                   </div>
               </MobileDataLabel>
            </template>
          </MobileCard>
        </template>
        <div v-else class="text-center py-12 text-zinc-500">
           <LinkIcon class="size-8 mx-auto mb-3 text-zinc-700" />
           Nenhum link UTM encontrado
        </div>
      </div>

      <!-- Pagination -->
      <Pagination @update:page="page => reloadAndGetAll({ page, perPage })" v-if="utmLinks?.pagination" :data="utmLinks?.pagination" v-model:per-page="perPage" />
    </TableContainer>
  </main>

  <DeleteConfirmationDialog v-model:open="isDeleteLinkDialogOpen" :loading="isDeletingLink" @confirm="isDeletingLink = true; destroy(deletingLinkId!).then(() => {
    toast({
      title: 'Link UTM excluído com sucesso',
      class: 'bg-green-500 text-white',
      duration: 1500
    })
    isDeletingLink = false
    isDeleteLinkDialogOpen = false
    const index = utmLinks?.data.findIndex(link => link.id == deletingLinkId)
    if (index !== -1) utmLinks?.data.splice(index!, 1)
  })" />

  <!-- New/Edit UTM Link Dialog -->
  <Dialog v-model:open="isLinkDialogOpen" @close="closeLinkDialog">
    <DialogContent class="sm:max-w-xl">
      <DialogHeader>
        <DialogTitle>{{ editingLinkId ? 'Editar link UTM' : 'Novo link UTM' }}</DialogTitle>
        <DialogDescription>
          Configure as informações do link UTM.
        </DialogDescription>
      </DialogHeader>
      <div class="grid gap-6 py-4">
        <div class="grid gap-2">
          <Label>Nome<span class="text-red-500">*</span></Label>
          <Input v-model="utmLinkForm.name" placeholder="Ex: Link Y" />
        </div>
        <div class="grid gap-2">
          <Label>Nome da campanha <span class="text-red-500">*</span></Label>
          <Input v-model="utmLinkForm.campaignName" placeholder="Ex: Vendas 2024/07/10|12635162..." />
        </div>
        <div class="grid gap-2">
          <Label>Origem <span class="text-red-500">*</span></Label>
          <Input v-model="utmLinkForm.source" placeholder="Ex: FB" />
        </div>
        <div class="grid gap-2">
          <Label>Meio <span class="text-red-500">*</span></Label>
          <Input v-model="utmLinkForm.medium" placeholder="Ex: ABO|1273612873681723" />
        </div>
        <div class="grid gap-2">
          <Label>Conteúdo <span class="text-red-500">*</span></Label>
          <Input v-model="utmLinkForm.content" placeholder="Ex: VIDEO 01|2412937293769713" />
        </div>
        <div class="grid gap-2">
          <Label>Palavra-Chave <span class="text-red-500">*</span></Label>
          <Input v-model="utmLinkForm.leadStartParameterContent" placeholder="Ex: Instagram_Reels" />
        </div>
      </div>
      <DialogFooter>
        <Button class="text-white" variant="ghost" @click="closeLinkDialog">Cancelar</Button>
        <Button :disabled="isSaving || !isFormValid" @click="saveUtmLink">
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          {{ editingLinkId ? 'Salvar alterações' : 'Cadastrar link' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  
</template>
  
<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import {
  ArrowsUpDownIcon,
  LinkIcon,
  ClipboardIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'
import MobileCard from '@/components/ui/mobile-card/MobileCard.vue'
import MobileDataLabel from '@/components/ui/mobile-card/MobileDataLabel.vue'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import RegisterButton from '@/components/ui/register-button/RegisterButton.vue'
import Table from '@/components/ui/table/Table.vue'
import TableHeader from '@/components/ui/table/TableHeader.vue'
import TableBody from '@/components/ui/table/TableBody.vue'
import TableRow from '@/components/ui/table/TableRow.vue'
import TableHead from '@/components/ui/table/TableHead.vue'
import TableFallback from '@/components/ui/table/TableFallback.vue'
import TableCell from '@/components/ui/table/TableCell.vue'
import Pagination from '@/components/ui/pagination/Pagination.vue'
import TableContainer from '@/components/ui/table/TableContainer.vue'
import { GetAllUtmLinksParams, useUtmLink } from '@/composables/useUtmLink'
import { Pagination as IPagination } from '@/interfaces/Pagination'
import { UtmLink } from '@/interfaces/UtmLink'
import TableRowSkeleton from '@/components/ui/table/TableRowSkeleton.vue'
import { useToast } from '@/components/ui/toast'
import { CheckIcon, Loader2 } from 'lucide-vue-next'
import DeleteConfirmationDialog from '@/components/ui/delete-dialog/DeleteConfirmationDialog.vue'
import PageTitle from '@/components/ui/page/PageTitle.vue'
import PageDescription from '@/components/ui/page/PageDescription.vue'
import { useBot } from '../../composables/useBot'

// Table Configuration
const tableHeaders = [
  { key: 'name', label: 'Nome', sortable: false },
  { key: 'campaignName', label: 'Campanha', sortable: false },
  { key: 'source', label: 'Origem', sortable: false },
  { key: 'medium', label: 'Meio', sortable: false },
  { key: 'content', label: 'Conteúdo', sortable: false },
  { key: 'startParam', label: 'Palavra-Chave', sortable: false },
  { key: 'link', label: 'Link', sortable: false },
  { key: 'actions', label: '', class: 'w-32' },
]

// Data
const utmLinks = ref<IPagination<UtmLink>>()
const { getAll, create, update, destroy } = useUtmLink()
const { botLink } = useBot()
const { toast } = useToast()

// Dialog and Form
const isSaving = ref(false)
const isLinkDialogOpen = ref(false)
const editingLinkId = ref<number | null>(null)
const deletingLinkId = ref<number | null>(null)
const isDeleteLinkDialogOpen = ref(false)
const isDeletingLink = ref(false)
const utmLinkForm = ref({
  name: '',
  campaignName: '',
  source: '',
  medium: '',
  content: '',
  leadStartParameterContent: ''
})
const perPage = ref('10')

// Computed
const isFormValid = computed(() => {
  return utmLinkForm.value.campaignName && 
          utmLinkForm.value.source && 
          utmLinkForm.value.medium && 
          utmLinkForm.value.content && 
          utmLinkForm.value.leadStartParameterContent
})

// Utils
// const copyLink = async (url: string) => {
//   await navigator.clipboard.writeText(url)
//   toast({
//     title: 'Link copiado!',
//     class: 'bg-green-500 text-white',
//     duration: 1500
//   })
// }

// Actions
const openNewLinkDialog = () => {
  editingLinkId.value = null
  utmLinkForm.value = {
    name: '',
    campaignName: '',
    source: '',
    medium: '',
    content: '',
    leadStartParameterContent: ''
  }
  isLinkDialogOpen.value = true
}

const closeLinkDialog = () => {
  isLinkDialogOpen.value = false
}

const editUtmLink = (link: UtmLink) => {
  editingLinkId.value = link.id
  utmLinkForm.value = {
    name: link.name,
    campaignName: link.campaignName,
    source: link.source,
    medium: link.medium,
    content: link.content,
    leadStartParameterContent: link.leadStartParameter!.content
  }
  isLinkDialogOpen.value = true
}

const saveUtmLink = () => {
  isSaving.value = true

  if (!editingLinkId.value) {
    create(utmLinkForm.value).then((newLink) => {
      isSaving.value = false
      toast({
        title: 'Link UTM criado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
      getAll().then((data) => utmLinks.value = data)
      closeLinkDialog()
    }).catch(() => {
      isSaving.value = false
    })
  } else {
    update(editingLinkId.value, utmLinkForm.value).then(() => {
      isSaving.value = false
      toast({
        title: 'Link UTM editado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
      const index = utmLinks.value?.data.findIndex(link => link.id == editingLinkId.value)
      if (index !== -1 && index !== null && index !== undefined) {
        utmLinks.value!.data[index] = {
          ...utmLinks.value!.data[index],
          ...utmLinkForm.value
        }
      }
      closeLinkDialog()
    }).catch(() => {
      isSaving.value = false
    })
  }
}

const deleteLink = (link: UtmLink) => {
  isDeleteLinkDialogOpen.value = true
  deletingLinkId.value = link.id
}

const sortBy = (key: string) => {
  // Implement sorting logic if needed
}

const reloadAndGetAll = (params: GetAllUtmLinksParams) => {
  utmLinks.value = undefined
  getAll(params).then((data) => utmLinks.value = data)
}

const copiedLink = ref<string | null>(null)

// Atualize a função copyLink
const copyLink = async (url: string) => {
  await navigator.clipboard.writeText(url)
  copiedLink.value = url
  setTimeout(() => {
    copiedLink.value = null
  }, 2000)
  
  toast({
    title: 'Link copiado!',
    class: 'bg-green-500 text-white',
    duration: 1500
  })
}

watch(() => perPage.value, () => {
  reloadAndGetAll({ perPage: perPage.value })
})

onMounted(() => {
  getAll().then((data) => utmLinks.value = data)
})
</script>